"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeActionsPromotions = computeActionsPromotions;
const cents_1 = require("../money/cents");
const validate_1 = require("./validate");
function sortedByPrice(units, direction) {
    return [...units].sort((a, b) => direction === 'asc'
        ? a.unitPrice - b.unitPrice || a.itemIndex - b.itemIndex
        : b.unitPrice - a.unitPrice || a.itemIndex - b.itemIndex);
}
function matchesCondition(unit, condition) {
    const expectedKind = condition.kind === 'PACKS' ? 'pack' : 'product';
    return unit.kind === expectedKind && condition.ids.includes(unit.referenceId);
}
function directionFor(strategy) {
    return strategy === 'ALL' || strategy === 'CHEAPEST' ? 'asc' : 'desc';
}
// v1 selectCandidatesForDiscount : par groupe, on prend les candidats encore
// disponibles, triés par le sens de la stratégie, tronqués à `quantity` ; s'il
// en manque, toute la promotion tombe. L'union est retriée à la fin.
function selectForGroups(groups, pool, direction) {
    let available = [...pool];
    const selected = [];
    for (const group of groups) {
        const candidates = sortedByPrice(available.filter((unit) => matchesCondition(unit, group)), direction);
        const window = candidates.slice(0, group.quantity);
        if (window.length < group.quantity)
            return undefined;
        available = available.filter((unit) => !window.includes(unit));
        selected.push(...window);
    }
    return { units: sortedByPrice(selected, direction), pool: available };
}
function itemDiscountFor(unitPrice, action) {
    if (action.type === 'ITEMS_DISCOUNT_PERCENTAGE') {
        (0, validate_1.assertPromotionPercent)(action.value, 'action.ITEMS_DISCOUNT_PERCENTAGE.value');
        return Math.min(unitPrice, (0, cents_1.applyPercent)(unitPrice, action.value));
    }
    (0, validate_1.assertPromotionAmount)(action.value, 'action.ITEMS_DISCOUNT_FIXED.value');
    return Math.min(unitPrice, action.value);
}
function register(discounts, unit, discount) {
    const existing = discounts.get(unit.referenceId);
    if (existing === undefined) {
        discounts.set(unit.referenceId, {
            referenceId: unit.referenceId,
            unitPrice: unit.unitPrice,
            discount,
            quantity: 1,
            discountedPrice: Math.max(0, unit.unitPrice - discount),
        });
        return;
    }
    existing.discount += discount;
    existing.quantity += 1;
    existing.discountedPrice = Math.max(0, existing.unitPrice - existing.discount);
}
function satisfiesOrderTotal(condition, total) {
    return condition.operator === 'gte' ? total >= condition.value : total <= condition.value;
}
function zeroed(promotionId) {
    return {
        promotionId,
        family: 'ACTIONS',
        orderDiscount: 0,
        itemsDiscounts: [],
        totalDiscount: 0,
    };
}
function rejected(promotion) {
    return {
        computed: zeroed(promotion.id),
        totalItemsDiscount: 0,
        orderTotalCondition: promotion.orderTotalCondition,
    };
}
function computeActionsPromotions(units, promotions, subtotal) {
    if (promotions.length === 0)
        return [];
    let pool = [...units];
    let currentTotal = subtotal;
    const results = [...promotions]
        .sort((a, b) => b.priority - a.priority)
        .map((promotion) => {
        // Une promotion rejetée ne consomme rien : le vivier d'avant lui est
        // rendu tel quel à la promotion suivante.
        const poolBeforePromotion = pool;
        const itemsDiscounts = new Map();
        const treatedTargets = new Set();
        let orderDiscount = 0;
        let failed = false;
        for (const action of promotion.actions) {
            if (action.type === 'ORDER_DISCOUNT_FIXED') {
                (0, validate_1.assertPromotionAmount)(action.value, 'action.ORDER_DISCOUNT_FIXED.value');
                orderDiscount += action.value;
                continue;
            }
            if (action.type === 'ORDER_DISCOUNT_PERCENTAGE') {
                (0, validate_1.assertPromotionPercent)(action.value, 'action.ORDER_DISCOUNT_PERCENTAGE.value');
                orderDiscount += (0, cents_1.applyPercent)(Math.max(0, currentTotal), action.value);
                continue;
            }
            const groups = promotion.conditions.filter((condition) => action.targets.includes(condition.referenceId));
            const selection = selectForGroups(groups, pool, directionFor(action.strategy));
            if (selection === undefined) {
                failed = true;
                break;
            }
            const targetIndex = action.strategy === 'BEST_VALUE' && selection.units.length > 1 ? 1 : 0;
            selection.units.forEach((unit, index) => {
                const discount = action.strategy === 'ALL' || index === targetIndex
                    ? itemDiscountFor(unit.unitPrice, action)
                    : 0;
                register(itemsDiscounts, unit, discount);
            });
            pool = selection.pool;
            for (const target of action.targets)
                treatedTargets.add(target);
        }
        // v1 : les groupes qu'aucune action n'a traités doivent rester
        // satisfiables sur le vivier restant (ils ne sont pas consommés).
        if (!failed) {
            const untreated = promotion.conditions.filter((condition) => !treatedTargets.has(condition.referenceId));
            if (untreated.length > 0 && selectForGroups(untreated, pool, 'asc') === undefined) {
                failed = true;
            }
        }
        if (failed) {
            pool = poolBeforePromotion;
            return rejected(promotion);
        }
        const discounts = [...itemsDiscounts.values()];
        const totalItemsDiscount = discounts.reduce((sum, discount) => sum + discount.discount, 0);
        const totalDiscount = orderDiscount + totalItemsDiscount;
        currentTotal -= totalDiscount;
        return {
            computed: {
                promotionId: promotion.id,
                family: 'ACTIONS',
                orderDiscount,
                itemsDiscounts: discounts,
                totalDiscount,
            },
            totalItemsDiscount,
            orderTotalCondition: promotion.orderTotalCondition,
        };
    });
    // Réajustement par le total de commande (v1 adjustComputedPromotionsByOrderTotal) :
    // la condition est réévaluée sur le total diminué des seules remises article
    // de la promotion, puis le total est diminué de sa remise totale.
    let total = subtotal;
    return results.map(({ computed, totalItemsDiscount, orderTotalCondition }) => {
        const localTotal = total - totalItemsDiscount;
        const applicable = orderTotalCondition === undefined || satisfiesOrderTotal(orderTotalCondition, localTotal);
        total -= computed.totalDiscount;
        return applicable ? computed : zeroed(computed.promotionId);
    });
}
